import { useState, useCallback } from 'react'
import { Modal, btnPrimary, btnSecondary, colors, input, label as labelStyle } from '../../components/ui'
import { api, ApiError } from '../../services/api'

export interface TenantSecretRow {
  readonly id: string
  readonly secretKey: string
  readonly description: string | null
  readonly lastAccessedAt: string | null
  readonly lastRotatedAt: string | null
  readonly createdAt: string
  readonly createdByName: string | null
}

function Field({
  label,
  required,
  children,
  hint,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
  hint?: string
}) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ ...labelStyle, display: 'block', marginBottom: '4px' }}>
        {label}
        {required && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
      </label>
      {children}
      {hint && <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '4px' }}>{hint}</div>}
    </div>
  )
}

export function SecretRevealModal({
  secretKey,
  value,
  onClose,
}: {
  readonly secretKey: string
  readonly value: string
  readonly onClose: () => void
}) {
  const [copied, setCopied] = useState(false)
  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(value)
    setCopied(true)
  }, [value])

  return (
    <Modal isOpen={true} onClose={onClose} title={`Secret Created — ${secretKey}`} width="620px">
      <div style={{ padding: '1.25rem' }}>
        <div
          style={{
            background: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '6px',
            padding: '0.75rem 1rem',
            marginBottom: '1.25rem',
            fontSize: '0.875rem',
            color: '#92400e',
          }}
        >
          This value will NOT be shown again. Copy it now.
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <div
            style={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              marginBottom: '6px',
            }}
          >
            Secret Value
          </div>
          <div
            style={{
              background: '#1e293b',
              color: '#e2e8f0',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              padding: '0.75rem',
              borderRadius: '6px',
              wordBreak: 'break-all',
              marginBottom: '0.5rem',
            }}
            data-testid="secret-plaintext"
          >
            {value}
          </div>
          <button style={{ ...btnPrimary, fontSize: '0.8125rem' }} onClick={handleCopy}>
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
        </div>
        <div style={{ fontSize: '0.8125rem', color: colors.textMuted, marginBottom: '1rem' }}>
          Reference this secret in EHR config fields as <code>tenant_secret:{secretKey}</code>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button style={btnSecondary} onClick={onClose}>
            I have copied the value — Close
          </button>
        </div>
      </div>
    </Modal>
  )
}

export function TenantSecretModal({
  onClose,
  onCreated,
}: {
  readonly onClose: () => void
  readonly onCreated: (secretKey: string, value: string) => void
}) {
  const [secretKey, setSecretKey] = useState('')
  const [value, setValue] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = useCallback(async () => {
    if (!secretKey.trim()) return setError('Secret key is required')
    if (!/^[A-Z0-9_]+$/.test(secretKey.trim())) {
      return setError('Key must be UPPER_SNAKE_CASE (A-Z, 0-9, underscores)')
    }
    if (!value.trim()) return setError('Value is required')
    setSaving(true)
    setError(null)
    try {
      await api.post('/tenant-secrets', {
        secretKey: secretKey.trim(),
        value: value.trim(),
        description: description.trim() || undefined,
      })
      onCreated(secretKey.trim(), value.trim())
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create secret')
      setSaving(false)
    }
  }, [secretKey, value, description, onCreated])

  return (
    <Modal isOpen={true} onClose={onClose} title="Add Secret" width="520px">
      <div style={{ padding: '1.25rem' }}>
        {error && (
          <div
            style={{
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              padding: '0.625rem 0.875rem',
              marginBottom: '1rem',
              fontSize: '0.875rem',
              color: '#991b1b',
            }}
          >
            {error}
          </div>
        )}

        <Field label="Secret Key" required hint="UPPER_SNAKE_CASE, e.g. CALREDIE_SFTP_PASSWORD">
          <input
            style={input}
            value={secretKey}
            onChange={(e) => setSecretKey(e.target.value.toUpperCase())}
            placeholder="e.g. CLIENT_A_SFTP_PASSWORD"
            data-testid="secret-key-input"
          />
        </Field>

        <Field label="Value" required>
          <input
            style={input}
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Secret value (write-only)"
            data-testid="secret-value-input"
          />
        </Field>

        <Field label="Description" hint="Optional note for your team">
          <input
            style={input}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. SFTP password for CalREDIE production"
            data-testid="secret-description-input"
          />
        </Field>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '1rem' }}>
          <button style={btnSecondary} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button style={btnPrimary} onClick={handleSubmit} disabled={saving} data-testid="create-secret-btn">
            {saving ? 'Creating...' : 'Create Secret'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
